import { EMPTY_STRING } from '../constants/Separators'
import { isStringNotEmpty } from './GlobalFunctions'

/**
 * Percent-encodes one byte of a binary string, e.g. `'é'` becomes `'%e9'`.
 *
 * @param char - A character of a binary string, whose code is a single byte (0-255).
 * @returns The byte as `%xx`.
 */
const toPercentEncodedByte = (char: string): string => {
    const hex: string = char.charCodeAt(0).toString(16)

    return hex.length === 1 ? `%0${hex}` : `%${hex}`
}

/**
 * Decodes a base64url string, reading the bytes it encodes as UTF-8.
 *
 * `window.atob` alone returns a binary (Latin-1) string, which garbles any non-ASCII character. Percent-encoding each
 * byte and passing the result to `decodeURIComponent` reads them as UTF-8 instead, and works where `TextDecoder` is not
 * available, such as Jest's jsdom environment.
 *
 * @param encoded - The base64url text, with or without padding.
 * @returns The decoded text.
 * @throws If the input is not valid base64, or its bytes are not valid UTF-8.
 */
const decodeBase64UrlAsUtf8 = (encoded: string): string => {
    const binary: string = window.atob(encoded.replace(/-/g, '+').replace(/_/g, '/'))

    return decodeURIComponent(binary.split(EMPTY_STRING).map(toPercentEncodedByte).join(EMPTY_STRING))
}

/**
 * Decodes the claims of a JWT, without verifying it, as UTF-8 JSON, the way the backend reads them.
 *
 * Only for reading a value back, never for trusting it: the signature is not checked.
 *
 * @param token - The JWT, as `header.payload.signature`.
 * @returns The claims, or `null` if the token is empty, malformed, or its payload is not a JSON object.
 */
export const decodeJwtClaims = (token: Nullable<string>): OnlyNullable<Record<string, unknown>> => {
    if (!isStringNotEmpty(token)) return null

    try {
        const claims: unknown = JSON.parse(decodeBase64UrlAsUtf8(token.split('.')[1]))

        return typeof claims === 'object' && claims !== null && !Array.isArray(claims)
            ? (claims as Record<string, unknown>)
            : null
    } catch {
        return null
    }
}

/**
 * Reads a claim as text, the way the backend's Jackson `asText()` reads a token's claims: a string as it is, and a
 * number as its decimal text (`1001` becomes `"1001"`). An empty string, or any other kind of value, gives `null`.
 *
 * @param claims - Claims from `decodeJwtClaims`, may be null.
 * @param name - The claim to read.
 * @returns The claim as text, or `null` if it is absent, empty, or neither a string nor a number.
 */
export const getClaimAsText = (claims: OnlyNullable<Record<string, unknown>>, name: string): OnlyNullable<string> => {
    const value: unknown = claims !== null ? claims[name] : undefined

    if (typeof value === 'number') return isFinite(value) ? String(value) : null

    return typeof value === 'string' && isStringNotEmpty(value) ? value : null
}
